import type { HubMap } from "../types";

export type TrophyTier = "bronze" | "silver" | "gold" | "platinum";
export type TrophyTrack = "crowd" | "escape" | "lineage" | "nightmare";

export interface TrophyDefinition {
  id: string;
  track: TrophyTrack;
  tier: TrophyTier;
  name: string;
  description: string;
  threshold: number;
  material: string;
}

export interface TrophyResult extends TrophyDefinition {
  unlocked: boolean;
  current: number;
  progress: string;
}

const TIERS: TrophyTier[] = ["bronze", "silver", "gold", "platinum"];
const MATERIALS: Record<TrophyTier, string> = {
  bronze: "Hammered bronze",
  silver: "Engraved silver",
  gold: "Jeweled gold",
  platinum: "Luminous platinum",
};

const TRACKS: Array<{
  track: Exclude<TrophyTrack, "nightmare">;
  name: string;
  description: string;
  thresholds: number[];
}> = [
  {
    track: "crowd",
    name: "Crowd Magnet",
    description: "Unique players attempted your verified mazes.",
    thresholds: [100, 1_000, 10_000, 50_000],
  },
  {
    track: "escape",
    name: "Escape Architect",
    description: "Players escaped your verified mazes.",
    thresholds: [25, 250, 1_000, 5_000],
  },
  {
    track: "lineage",
    name: "Lineage Founder",
    description: "Creators remixed your verified mazes.",
    thresholds: [5, 25, 100, 500],
  },
];

export const TROPHY_DEFINITIONS: TrophyDefinition[] = [
  ...TRACKS.flatMap((track) =>
    track.thresholds.map((threshold, index) => ({
      id: `trophy_${track.track}_${TIERS[index]}`,
      track: track.track,
      tier: TIERS[index],
      name: `${track.name} ${TIERS[index]}`,
      description: track.description,
      threshold,
      material: MATERIALS[TIERS[index]],
    })),
  ),
  ...[100, 500, 2_000, 10_000].map((threshold, index) => ({
    id: `trophy_nightmare_${TIERS[index]}`,
    track: "nightmare" as const,
    tier: TIERS[index],
    name: `Nightmare Smith ${TIERS[index]}`,
    description: "A single verified maze held an exceptionally low escape rate.",
    threshold,
    material: MATERIALS[TIERS[index]],
  })),
];

const tierClearRate: Record<TrophyTier, number> = {
  bronze: 0.1,
  silver: 0.05,
  gold: 0.02,
  platinum: 0.01,
};

export function evaluateTrophies(maps: HubMap[]): TrophyResult[] {
  const verified = maps.filter((map) => map.verified);
  const totals = {
    crowd: verified.reduce((sum, map) => sum + map.uniqueAttemptCount, 0),
    escape: verified.reduce((sum, map) => sum + map.escapeCount, 0),
    lineage: verified.reduce((sum, map) => sum + map.remixCount, 0),
  };
  return TROPHY_DEFINITIONS.map((definition) => {
    if (definition.track === "nightmare") {
      const qualifying = verified.filter((map) => {
        const attempts = Math.max(0, map.uniqueAttemptCount);
        const rate = attempts ? map.escapeCount / attempts : 1;
        return attempts >= definition.threshold && rate <= tierClearRate[definition.tier];
      });
      const current = verified.reduce((best, map) => {
        const attempts = Math.max(0, map.uniqueAttemptCount);
        const rate = attempts ? map.escapeCount / attempts : 1;
        return rate <= tierClearRate[definition.tier] ? Math.max(best, attempts) : best;
      }, 0);
      return {
        ...definition,
        unlocked: qualifying.length > 0,
        current,
        progress: `${Math.min(current, definition.threshold).toLocaleString()} / ${definition.threshold.toLocaleString()} attempts at ≤${Math.round(tierClearRate[definition.tier] * 100)}% escapes`,
      };
    }
    const current = totals[definition.track];
    return {
      ...definition,
      unlocked: current >= definition.threshold,
      current,
      progress: `${Math.min(current, definition.threshold).toLocaleString()} / ${definition.threshold.toLocaleString()}`,
    };
  });
}
